import { createSlice } from "@reduxjs/toolkit";
import {
  FetcherBrand,
  FetcherConfig,
  FetcherInterface,
  FetcherLookup,
  FetcherObject,
  FetcherReport,
} from "../components/DataCollection/FetcherSettings/helpers/fetcherSettingsDummyData";

export interface FetcherSettingsSlice {
  interfaces: FetcherInterface[];
  reports: FetcherReport[];
  configs: FetcherConfig[];
  objects: FetcherObject[];
  brands: FetcherBrand[];
  linkok: string[];
  collectionMethods: FetcherLookup[];
  subFilteringMethods: FetcherLookup[];
  formatTypes: FetcherLookup[];
  collectionTypes: FetcherLookup[];
  isCatalogueLoading: boolean;
  fetcherSettingsMainTablesData: any;
  fetcherSettingsDetailsData?: any;
  isDetailsLoading: boolean;
  fetcherSettingsDevObjsData: any;
  fetcherSettingsBrandTraceData: any;
}

const initialState: FetcherSettingsSlice = {
  interfaces: [],
  reports: [],
  configs: [],
  objects: [],
  brands: [],
  linkok: [],
  collectionMethods: [],
  subFilteringMethods: [],
  formatTypes: [],
  collectionTypes: [],
  isCatalogueLoading: false,
  fetcherSettingsMainTablesData: null,
  fetcherSettingsDetailsData: null,
  isDetailsLoading: false,
  fetcherSettingsDevObjsData: null,
  fetcherSettingsBrandTraceData: null,
};

/**
 * Keeps the rollup counts each table shows in step with the rows actually held,
 * the way the read query recomputes them server-side. Called after every local
 * write so a created report immediately shows up in its interface's counters.
 */
function recount(state: FetcherSettingsSlice) {
  state.reports.forEach((report) => {
    const own = state.objects.filter(
      (object) => object.lvl === "report_id" && object.mid === report.id,
    );
    const configs = state.configs.filter(
      (config) => config.report === report.id,
    );
    const brandIds = new Set<string>();
    configs.forEach((config) =>
      config.brands.forEach((brand) => brandIds.add(brand.id)),
    );
    report.objects = own.length;
    report.configs = configs.length;
    report.brands = brandIds.size;
    report.upath = own.find((object) => object.k === "url_path")?.v ?? "";
  });

  state.interfaces.forEach((backendInterface) => {
    const reports = state.reports.filter(
      (report) => report.iface === backendInterface.id,
    );
    const configs = state.configs.filter((config) =>
      reports.some((report) => report.id === config.report),
    );
    const brandIds = new Set<string>();
    configs.forEach((config) =>
      config.brands.forEach((brand) => brandIds.add(brand.id)),
    );
    backendInterface.reports = reports.length;
    backendInterface.configs = configs.length;
    backendInterface.brands = brandIds.size;
    backendInterface.objects = state.objects.filter(
      (object) =>
        object.lvl === "interface_id" && object.mid === backendInterface.id,
    ).length;
  });
}

const fetcherSettingsSlice = createSlice({
  name: "fetcherSettings",
  initialState,
  reducers: {
    setFetcherCatalogue: (state, action) => {
      state.interfaces = action.payload.interfaces;
      state.reports = action.payload.reports;
      state.configs = action.payload.configs;
      state.objects = action.payload.objects;
      state.brands = action.payload.brands;
      state.linkok = action.payload.linkok;
      state.collectionMethods = action.payload.cm;
      state.subFilteringMethods = action.payload.sfm;
      state.formatTypes = action.payload.ft;
      state.collectionTypes = action.payload.ct;
      recount(state);
    },
    setIsCatalogueLoading: (state, action) => {
      state.isCatalogueLoading = action.payload;
    },
    setIsDetailsLoading: (state, action) => {
      state.isDetailsLoading = action.payload;
    },
    setFetcherSettingsMainTablesData: (state, action) => {
      state.fetcherSettingsMainTablesData = action.payload;
    },
    setFetcherSettingsDetailsData: (state, action) => {
      state.fetcherSettingsDetailsData = action.payload;
    },
    setFetcherSettingsDevObjsData: (state, action) => {
      state.fetcherSettingsDevObjsData = action.payload;
    },
    setFetcherSettingsBrandTraceData: (state, action) => {
      state.fetcherSettingsBrandTraceData = action.payload;
    },
    upsertFetcherInterface: (state, action) => {
      const index = state.interfaces.findIndex(
        (row) => row.id === action.payload.id,
      );
      if (index >= 0) state.interfaces[index] = action.payload;
      else state.interfaces.unshift(action.payload);
      recount(state);
    },
    removeFetcherInterface: (state, action) => {
      state.interfaces = state.interfaces.filter(
        (row) => row.id !== action.payload,
      );
      state.objects = state.objects.filter(
        (row) => !(row.lvl === "interface_id" && row.mid === action.payload),
      );
      recount(state);
    },
    upsertFetcherReport: (state, action) => {
      const index = state.reports.findIndex(
        (row) => row.id === action.payload.id,
      );
      if (index >= 0) state.reports[index] = action.payload;
      else state.reports.unshift(action.payload);
      recount(state);
    },
    removeFetcherReport: (state, action) => {
      state.reports = state.reports.filter((row) => row.id !== action.payload);
      state.objects = state.objects.filter(
        (row) => !(row.lvl === "report_id" && row.mid === action.payload),
      );
      recount(state);
    },
    upsertFetcherConfig: (state, action) => {
      const index = state.configs.findIndex(
        (row) => row.id === action.payload.id,
      );
      if (index >= 0) state.configs[index] = action.payload;
      else state.configs.unshift(action.payload);
      recount(state);
    },
    removeFetcherConfig: (state, action) => {
      state.configs = state.configs.filter((row) => row.id !== action.payload);
      recount(state);
    },
    upsertFetcherObjects: (state, action) => {
      (action.payload as FetcherObject[]).forEach((object) => {
        const index = state.objects.findIndex((row) => row.id === object.id);
        if (index >= 0) state.objects[index] = object;
        else state.objects.push(object);
      });
      recount(state);
    },
    removeFetcherObjects: (state, action) => {
      const ids: string[] = action.payload;
      state.objects = state.objects.filter((row) => !ids.includes(row.id));
      recount(state);
    },
  },
});

export const {
  setFetcherCatalogue,
  setIsCatalogueLoading,
  setIsDetailsLoading,
  upsertFetcherInterface,
  removeFetcherInterface,
  upsertFetcherReport,
  removeFetcherReport,
  upsertFetcherConfig,
  removeFetcherConfig,
  upsertFetcherObjects,
  removeFetcherObjects,
  setFetcherSettingsMainTablesData,
  setFetcherSettingsDetailsData,
  setFetcherSettingsDevObjsData,
  setFetcherSettingsBrandTraceData,
} = fetcherSettingsSlice.actions;

export default fetcherSettingsSlice.reducer;
